import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { getPasswordError } from '../lib/utils';
import { Lock, ArrowLeft } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const { toast } = useToast();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordError = getPasswordError(formData.newPassword);
    if (passwordError) {
      toast({ title: 'Invalid password', description: passwordError, variant: 'destructive' });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your new passwords match.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await resetPassword(token, formData.newPassword);
      if (result.success) {
        toast({
          title: 'Password reset',
          description: 'Your password has been reset. You can now log in.'
        });
        setIsDone(true);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast({
          title: 'Reset failed',
          description: result.error || 'The reset link may have expired. Request a new one.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Reset Password</h1>
            <p className="text-gray-600">Choose a new password for your account</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">Set New Password</CardTitle>
            </CardHeader>
            <CardContent>
              {!token ? (
                <div className="text-center space-y-4">
                  <p className="text-gray-600">
                    This reset link is missing its token. Please use the link from your email
                    or request a new one.
                  </p>
                  <Link to="/forgot-password">
                    <Button className="bg-blue-600 hover:bg-blue-700 mt-4">
                      Request New Link
                    </Button>
                  </Link>
                </div>
              ) : isDone ? (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Lock size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Password Updated</h3>
                  <p className="text-gray-600">Redirecting you to login&hellip;</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="newPassword" className="text-gray-900">
                      New Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Enter your new password"
                      className="mt-2"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Min 8 characters with uppercase, lowercase, a number and a special character.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-gray-900">
                      Confirm New Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your new password"
                      className="mt-2"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isSubmitting ? (
                      'Resetting...'
                    ) : (
                      <>
                        <Lock size={18} className="mr-2" />
                        Reset Password
                      </>
                    )}
                  </Button>

                  <div className="text-center text-sm">
                    <Link to="/login" className="text-blue-600 hover:text-blue-700 inline-flex items-center">
                      <ArrowLeft size={16} className="mr-1" />
                      Back to Login
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
